import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import billingService from '../services/billingService';
import { apiErrorMessage } from '../utils/apiError';
import { loadPdfjs } from '../utils/pdfjs';
import ConfirmModal from './shared/ConfirmModal';
import './CountersignDialog.css';

// COUNTERSIGNING A BILLING DOCUMENT (V666/V667) — the hotel's signed copy drawn page by page, the partner's signatures
// (Partner Settings › Templates › Billing › Signatures) as a palette beside it. Pick a signature, click where it goes,
// size it, Countersign: ui-v2-billing-documents puts that image there with the signer's name and the date under it,
// and adds a countersignature page. A dialog at the user's request (the one step of a flow, not a page of its own).
//
// The placement is sent as FRACTIONS of the page from its top-left — { page, xFrac, yFrac, widthFrac } — so it lands
// in the same place on the PDF whatever size the page is drawn at here.

const PAGE_WIDTH_PX = 480;   // fits the wide ConfirmModal (760px) beside the 12rem palette

const CountersignDialog = ({ hotelId, partnerId, doc, onDone, onClose }) => {
  const [pages, setPages] = useState(null);        // [{ url, width, height }] rendered to images
  const [signatures, setSignatures] = useState(null);
  const [signatureId, setSignatureId] = useState(null);
  const [placement, setPlacement] = useState(null); // { page, xFrac, yFrac }
  const [widthPct, setWidthPct] = useState(25);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The signed copy, drawn to one image per page.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await billingService.documentLink(hotelId, doc.id);
        const bytes = Uint8Array.from(atob(res.pdfBase64), c => c.charCodeAt(0));
        const pdfjs = await loadPdfjs();
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        const out = [];
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (PAGE_WIDTH_PX / base.width) * 2 }); // 2× for a sharp image
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          out.push({ url: canvas.toDataURL('image/png'), aspect: base.height / base.width });
        }
        if (live) setPages(out);
      } catch (err) {
        if (live) setError(apiErrorMessage(err, err.message || 'Could not open the signed copy'));
      }
    })();
    return () => { live = false; };
  }, [hotelId, doc.id]);

  useEffect(() => {
    let live = true;
    billingService.listSignatures(partnerId)
      .then(res => {
        if (!live) return;
        const list = res.signatures || [];
        setSignatures(list);
        if (list.length === 1) setSignatureId(list[0].id);
      })
      .catch(err => { if (live) setError(apiErrorMessage(err, 'Could not load the partner’s signatures')); });
    return () => { live = false; };
  }, [partnerId]);

  const chosen = (signatures || []).find(s => s.id === signatureId) || null;

  // A click puts the signature's TOP-LEFT where the pointer is, kept inside the page.
  const place = (pageIndex, e) => {
    if (!chosen) { setError('Choose a signature first.'); return; }
    setError(null);
    const rect = e.currentTarget.getBoundingClientRect();
    const xFrac = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1 - widthPct / 100);
    const yFrac = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 0.98);
    setPlacement({ page: pageIndex + 1, xFrac, yFrac });
  };

  const countersign = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await billingService.documentAction(hotelId, doc.id, {
        action: 'countersign', signatureId, placement: { ...placement, widthFrac: widthPct / 100 },
      });
      onDone(res.document);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not countersign the document'));
      setBusy(false);
    }
  };

  // In a portal: the Billing tab sits inside the hotel <form>, and ConfirmModal's buttons would submit it.
  return createPortal(
    <ConfirmModal
      title={`Countersign “${doc.templateName}”`}
      confirmLabel={busy ? 'Countersigning…' : 'Countersign'}
      confirmDisabled={busy || !chosen || !placement}
      onConfirm={countersign}
      onCancel={onClose}
      closeOnBackdrop={false}
      wide
    >
      <p className="csd-hint">
        Choose a signature, then click on the document where it goes. Your name and today’s date are printed under it,
        and a countersignature page is added.
      </p>
      <div className="csd-layout">
        <div className="csd-palette">
          <div className="csd-palette-title">Signatures</div>
          {!signatures ? <p className="csd-hint">Loading…</p>
            : signatures.length === 0 ? (
              <p className="csd-hint">No signatures yet — add one under Partner Settings › Templates › Billing.</p>
            ) : signatures.map(s => (
              <button key={s.id} type="button" className={`csd-signature${s.id === signatureId ? ' csd-signature-active' : ''}`}
                onClick={() => setSignatureId(s.id)} title={s.name}>
                <img src={s.imageDataUrl} alt={`Signature: ${s.name}`} />
                <span>{s.name}</span>
              </button>
            ))}
          {chosen && (
            <label className="csd-size">
              Size
              <input type="range" min="10" max="50" value={widthPct} onChange={e => setWidthPct(Number(e.target.value))} />
            </label>
          )}
        </div>
        <div className="csd-pages">
          {!pages ? <p className="csd-hint">{error ? '' : 'Opening the signed copy…'}</p> : pages.map((p, i) => (
            <div key={i} className="csd-page" style={{ width: PAGE_WIDTH_PX, height: PAGE_WIDTH_PX * p.aspect }}
              onClick={e => place(i, e)} role="presentation">
              <img src={p.url} alt={`Page ${i + 1}`} draggable={false} />
              {placement && placement.page === i + 1 && chosen && (
                <img className="csd-placed" src={chosen.imageDataUrl} alt="" draggable={false}
                  style={{ left: `${placement.xFrac * 100}%`, top: `${placement.yFrac * 100}%`, width: `${widthPct}%` }} />
              )}
            </div>
          ))}
        </div>
      </div>
      {error && <div className="acp-error-banner">{error}</div>}
    </ConfirmModal>,
    document.body,
  );
};

export default CountersignDialog;
